import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {useAuthStore} from '@/entities/session/auth-store';
import {contextToneAndLabel} from '@/entities/session/labels';
import {IconBubble} from '@/shared/ui/card';
import {Field, FieldGroup} from '@/shared/ui/field';
import {Icon} from '@/shared/ui/icons';
import {Screen} from '@/shared/ui/screen';
import {StatusPill} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * The stand-in for a tab that has nothing to show — in practice, a tab reached with no
 * active context, which the navigator should never mount.
 *
 * It is deliberately not an empty box: it prints the context and the company id the
 * screen WOULD load from, so a wrong context switch is visible here rather than later
 * when real data starts arriving from the wrong tenant. The id is mono — it is machine
 * data, and the reader is expected to compare it character by character.
 */
const styles = StyleSheet.create({
  body: {gap: spacing(5), paddingTop: spacing(6)},
  head: {alignItems: 'center', gap: spacing(3)},
  headText: {alignItems: 'center', gap: spacing(1)},
  centered: {textAlign: 'center'},
});

export function ContextPlaceholder({titleKey}: {titleKey: string}) {
  const {t} = useTranslation();
  const p = useTheme();
  const context = useAuthStore(s => s.activeContext);

  const badge = context ? contextToneAndLabel(context) : null;

  return (
    <Screen scroll edgeTop={false} style={styles.body} testID="placeholder-screen">
      <View style={styles.head}>
        <IconBubble tone={p.muted} size={64}>
          <Icon name="clock" size={30} color={p.mutedForeground} />
        </IconBubble>
        <View style={styles.headText}>
          <AppText variant="title" style={styles.centered}>
            {t(titleKey)}
          </AppText>
          <AppText variant="caption" style={styles.centered}>
            {t('stub.soon')}
          </AppText>
        </View>
      </View>

      <FieldGroup>
        {badge ? (
          <Field
            label={t('stub.context')}
            trailing={
              <StatusPill
                tone={badge.tone}
                size="sm"
                label={`${t(badge.kindKey)} · ${context?.title ?? ''}`}
              />
            }
          />
        ) : null}
        {context ? (
          <Field
            testID="placeholder-company-id"
            label={t('stub.companyId')}
            value={context.companyId}
            mono
          />
        ) : null}
      </FieldGroup>
    </Screen>
  );
}
